import React, { useState } from "react";
import { Button, message } from "antd";
import { CreditCardFilled } from "@ant-design/icons";
import { loadState, saveState } from "../../stores/store";
import { markApplicationComplete } from "../../api/user";
import { toggleInvoicePaidStatus } from "../../api/invoice";
import PropTypes from "prop-types";

const PaymentButton = ({
  invoices = [],
  settings,
  label = "Pay Now",
  allowZeroAutoComplete = true,
}) => {
  const [initializingTransaction, setInitializingTransaction] = useState(false);

  const initializeTransaction = async () => {
    try {
      setInitializingTransaction(true);

      const state = loadState();
      const user = state.user;

      const amount = invoices.reduce(
        (sum, invoice) => sum + (invoice?.fee?.total_fee_unpaid || 0),
        0
      );

      // Handle zero-amount cases
      if (amount === 0) {
        // For single "free" invoice we still want the old behaviour
        if (
          allowZeroAutoComplete &&
          invoices.length === 1 &&
          invoices[0]?._id
        ) {
          await toggleInvoicePaidStatus(invoices[0]._id, true);
          window.location.reload();
          return;
        }

        // For multi-invoice or disallowed auto-complete, just stop and show a message
        message.error(
          "There is no outstanding amount to pay for these invoices."
        );
        setInitializingTransaction(false);
        return;
      }

      const { profilePhoto, ...userWithoutProfilePhoto } = user;

      const response = await fetch("/api/initialize-transaction", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: userWithoutProfilePhoto.email,
          user: userWithoutProfilePhoto,
          amount: amount,
          url: window.location.origin,
          metadata: {
            invoice_ids: invoices.map((invoice) => invoice._id),
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      const pfHost = process.env.SANDBOX ? "sandbox.payfast.co.za" : "www.payfast.co.za";

      if (result.myData) {
        await markApplicationComplete(user._id);
        state.user.applicationCompleted = true;
        saveState(state);

        const myData = result.myData;
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `https://${pfHost}/eng/process`;

        for (let key in myData) {
          if (Object.prototype.hasOwnProperty.call(myData, key)) {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = myData[key];
            form.appendChild(input);
          }
        }

        document.body.appendChild(form);
        form.submit();
      } else {
        throw new Error(result.message || "Failed to initialize transaction");
      }
    } catch (error) {
      console.error("Error initializing transaction:", error);
      message.error(`Failed to initialize transaction: ${error.message}`);
      setInitializingTransaction(false);
    }
  };

  return (
    <Button
      type="primary"
      icon={<CreditCardFilled />}
      onClick={initializeTransaction}
      className="payment-button"
      loading={initializingTransaction}
    >
      {label}
    </Button>
  );
};

PaymentButton.defaultProps = {
  invoices: [],
  label: "Pay Now",
  allowZeroAutoComplete: true,
};

PaymentButton.propTypes = {
  invoices: PropTypes.array.isRequired,
  settings: PropTypes.object.isRequired,
  label: PropTypes.string,
  allowZeroAutoComplete: PropTypes.bool,
};

export default PaymentButton;
